import { usePatchUser } from '@vlabs/api/artemy/client';
import { CheckCircleIcon } from '@vlabs/icons';
import { useHasPermission } from '@vlabs/permissions';
import { Button } from '@vlabs/uikit/components/controls/button';
import type { ButtonProps } from '@vlabs/uikit/components/controls/button';
import { toast } from 'react-toastify';

import { buildTestid } from '@vlabs/monokit/shared/lib/utils';

import { useUnblockUserTranslation } from './i18n';
import type { UnblockUserProps } from './types';

type UnblockUserButtonProps = UnblockUserProps & Omit<ButtonProps, 'onClick' | 'variant'>;

const displayName = 'UnblockUserButton';
const testid = buildTestid(displayName);

function UnblockUserButton({
  userId,
  variant = 'unblock',
  'data-testid': dataTestId = undefined,
  ...props
}: UnblockUserButtonProps): JSX.Element | null {
  const { t } = useUnblockUserTranslation();

  const labelKey = variant === 'activate' ? 'activate_button' : 'button';
  const successKey = variant === 'activate' ? 'toast_success_activate' : 'toast_success';
  const errorKey = variant === 'activate' ? 'toast_error_activate' : 'toast_error';

  const unblockUser = usePatchUser({
    mutation: {
      onSuccess: () => {
        toast.success(t(successKey));
      },
      onError: () => {
        toast.error(t(errorKey));
      },
    },
  });

  const hasAccess = useHasPermission('users', 'update');
  if (!hasAccess) return null;

  const handleClick = () => {
    unblockUser.mutate({ userId, data: { status: 'active' } });
  };

  return (
    <Button
      disabled={unblockUser.isPending}
      icon={<CheckCircleIcon />}
      kind="positive"
      onClick={handleClick}
      {...testid(dataTestId)}
      {...props}
    >
      {t(labelKey)}
    </Button>
  );
}

UnblockUserButton.displayName = displayName;

export { UnblockUserButton };
